import {HexGrid, HexBearing, HexCoords, HexViewCoords} from '../grid';

import entityManager, { EntityManager } from "./entityManager";
import EntityFactory from './EntityFactory';
import ActionHistory from './ActionHistory';
import Action from './Action';
import { GameEvents } from './GameEvents';
import {
  HexPosition,
  PlayerControlled,
  WantsToAct,
  IntendsToAct,
  Faction,
  Dead,
} from '../components';
import { SpatialSystem, TurnSystem, AISystem, ActionSystem, InputSystem, TurnPredictionSystem, CleanupSystem } from '../systems';
import getLogger from '../lib/logger';

const logger = getLogger('LevelManager');

export default class LevelManager {

  constructor(eventEmitter, levelData, levelKey) {
    this.eventEmitter = eventEmitter;

    this.turnSystem = new TurnSystem();
    this.aiSystem = new AISystem();
    this.inputSystem = new InputSystem(eventEmitter);
    this.actionSystem = new ActionSystem();
    this.turnPredictionSystem = new TurnPredictionSystem();
    this.cleanupSystem = new CleanupSystem();
    this.actionHistory = new ActionHistory(entityManager);

    // Load level entities
    entityManager.resetAndLoad(JSON.stringify(levelData));

    // Initialize action history with initial snapshot
    this.actionHistory.init();
    this.eventEmitter.emit(GameEvents.MOVE_COUNT_UPDATED, {
      moveCount: this.actionHistory.getMoveCount(),
      undoCount: this.actionHistory.getUndoCount(),
    });

    // Spatial system must be after entities are loaded because it
    // initializes the spatial state in the constructor
    this.spatialSystem = new SpatialSystem(this.eventEmitter);

    // Emit level started event
    this.eventEmitter.emit(GameEvents.LEVEL_STARTED, { levelKey });
  }

  // Starts the game loop by running AI turns until the player's turn.
  async start() {
    this.runAITurns();
  }

  /**
   * This function is called by the InputSystem after the player has made a decision.
   * It processes the player's action and then kicks off the AI turn loop.
   */
  async advanceAfterPlayerAction() {
    // Clear player action highlights and intention indicators
    this.eventEmitter.emit(GameEvents.CLEAR_PLAYER_ACTION_HIGHLIGHTS);
    this.eventEmitter.emit(GameEvents.CLEAR_INTENTION_INDICATORS);

    await this._resolveAndCleanupActions();

    // Commit pending action with snapshot after resolution
    this.actionHistory.commitPendingAction();
    this.eventEmitter.emit(GameEvents.MOVE_COUNT_UPDATED, {
      moveCount: this.actionHistory.getMoveCount(),
      undoCount: this.actionHistory.getUndoCount(),
    });

    const gameStatus = this.checkWinLossConditions();
    if (gameStatus !== 'ongoing') {
      const moveCount = this.getMoveCount();
      const undoCount = this.actionHistory.getUndoCount();
      const playerSolution = this.actionHistory.getPlayerSolution();
      if (gameStatus === 'loss') {
        this.eventEmitter.dispatch(GameEvents.LEVEL_LOST, { moveCount, undoCount, playerSolution });
      } else if (gameStatus === 'win') {
        this.eventEmitter.dispatch(GameEvents.LEVEL_WON, { moveCount, undoCount, playerSolution });
      }
      return; // Stop the game flow.
    }

    // Now, run the AI turns until it's the player's turn again.
    this.runAITurns();
  }

  /**
   * This is the main game loop for non-player turns.
   * It's an iterative loop that runs until it's the player's turn to act.
   */
  async runAITurns() {
    // Loop indefinitely. The loop will break when it's the player's turn.
    while (true) {
      // Check for win/loss conditions at the start of each loop iteration.
      const gameStatus = this.checkWinLossConditions();
      if (gameStatus !== 'ongoing') {
        // Emit win/loss events regardless of whether spatial rendering exists (headless-first!)
        const moveCount = this.getMoveCount();
        const undoCount = this.actionHistory.getUndoCount();
        const playerSolution = this.actionHistory.getPlayerSolution();
        if (gameStatus === 'loss') {
          this.eventEmitter.dispatch(GameEvents.LEVEL_LOST, { moveCount, undoCount, playerSolution });
        } else if (gameStatus === 'win') {
          this.eventEmitter.dispatch(GameEvents.LEVEL_WON, { moveCount, undoCount, playerSolution });
        }
        break; // Exit the AI loop.
      }

      // 1. Advance turns until someone is ready to act.
      while (entityManager.query([WantsToAct]).length === 0) {
        this.turnSystem.update();
      }

      // 2. Predict turn order for the UI.
      // This must be done BEFORE the AI system runs, as the AI consumes its 'WantsToAct' component.
      this.turnPredictionSystem.update();

      // 3. Let AI make decisions.
      // This consumes the 'WantsToAct' component for AI entities.
      this.aiSystem.update();

      // 4. Check if we need to wait for the player.
      const playerIsWaiting = entityManager.query([PlayerControlled, WantsToAct]).length > 0;
      if (playerIsWaiting) {
        // It's the player's turn. Initialize the input system for their turn.
        this.inputSystem.initializePlayerTurn();
        // Update intention indicators and character state (including accumulated energy)
        this.spatialSystem.sync();

        // Break the loop and wait for input.
        break;
      }

      // 5. If we're still here, only AI actors acted.
      // Process their actions and continue the loop for the next turn cycle.
      await this._resolveAndCleanupActions();
    }
  }

  async _resolveAndCleanupActions() {
    const { resolvedActions, intendingActors, animationPlan } = this.actionSystem.resolveTick();

    if (animationPlan && animationPlan.length > 0) {
      // Dispatch async event - listeners call resolve() when done, or auto-resolves if headless
      await this.eventEmitter.dispatchAsync(GameEvents.START_ANIMATIONS, { plan: animationPlan });
    }
    this.actionSystem.applyOutcomes(resolvedActions, intendingActors);
    this.spatialSystem.sync();
    this.cleanupSystem.update();
  }

  /**
   * Checks for win or loss conditions.
   * @returns {'win' | 'loss' | 'ongoing'}
   */
  checkWinLossConditions() {
    // Loss condition: Player is dead.
    const playerEntity = entityManager.query([PlayerControlled])[0];
    if (!playerEntity || playerEntity.hasComponent(Dead)) {
      return 'loss';
    }

    // Win condition: No more monsters.
    const monsterEntities = entityManager.query([Faction]).filter(
      e => e.getComponent(Faction).name === 'monsters'
    );
    if (monsterEntities.length === 0) {
      return 'win';
    }

    return 'ongoing';
  }

  /**
   * Updates the visual highlights for possible actions based on the current input mode.
   */
  updateActionHighlights() {
    const actions = this.inputSystem.getPossiblePlayerActions(this.inputSystem.currentMode);
    const hexCoords = Array.from(actions.keys()).map(key => {
        const [q, r] = key.split('.').map(Number);
        return { q, r };
    });

    this.eventEmitter.emit(GameEvents.UPDATE_PLAYER_ACTION_HIGHLIGHTS, { hexCoords });
  }

  /**
   * Get the current move count for analytics.
   * @returns {number} Number of player actions taken in the current level
   */
  getMoveCount() {
    return this.actionHistory.getMoveCount();
  }

  /**
   * Record a player action and prepare it for snapshot capture.
   * Called by InputSystem on player action.
   */
  recordPlayerAction(action) {
    const player = entityManager.query([PlayerControlled, HexPosition])[0];
    if (!player) {
      logger.warn('Cannot record action: no player found');
      return;
    }

    const playerPos = player.getComponent(HexPosition).coords;

    // Store action in relative coordinates via ActionHistory
    this.actionHistory.recordPendingAction(action.toRelativeJSON(playerPos));
  }

  /**
   * Get a copy of the action history.
   * @returns {Array} Copy of action history entries
   */
  getActionHistory() {
    return this.actionHistory.getActionHistory();
  }

  /**
   * Get player's solution as saveable format (for future solution save feature).
   * @returns {Array} Array of actions in solution format
   */
  getPlayerSolution() {
    return this.actionHistory.getPlayerSolution();
  }

  /**
   * Undo the last player move by restoring the previous snapshot.
   */
  async undoLastMove() {
    // Undo via ActionHistory - returns affected entity IDs or null
    const affectedEntityIds = this.actionHistory.undoLastAction();

    if (!affectedEntityIds) {
      logger.warn('Cannot undo: no action history');
      return;
    }

    // Emit move count update after successful undo
    this.eventEmitter.emit(GameEvents.MOVE_COUNT_UPDATED, {
      moveCount: this.actionHistory.getMoveCount(),
      undoCount: this.actionHistory.getUndoCount(),
    });

    // Update rendering to match restored state
    // Sync character state after undo - this triggers LevelScene to update sprites reactively
    this.spatialSystem.sync();

    // Restart the game loop - it will run AI turns and stop at player's turn
    this.runAITurns();
  }

  /**
   * Clean up resources.
   */
  destroy() {
    // Clear action history on level end
    this.actionHistory.clear();
  }

  /**
   * Delegate to InputSystem - called by LevelHandler
   */
  setActionMode(mode) {
    this.inputSystem.setActionMode(mode);
  }

  /**
   * Delegate to InputSystem - called by LevelHandler
   */
  async submitPlayerAction(action) {
    this.inputSystem.submitPlayerAction(action);

    // Emit event to record action for history
    this.recordPlayerAction(action)
    // Clear enemy patterns when action is submitted
    this.eventEmitter.emit(GameEvents.CLEAR_ENEMY_PATTERN_HIGHLIGHTS);

    await this.advanceAfterPlayerAction();
  }

  /**
   * Convert relative action JSON to absolute coords and submit.
   * Used by solution replay - follows same pattern as dev SolutionPlayer.
   */
  async submitRelativeAction(relativeActionData) {
    const player = entityManager.query([PlayerControlled, WantsToAct])[0];
    if (!player) {
      logger.warn('submitRelativeAction: no acting player found');
      return;
    }

    // Convert relative coords to absolute using current player position
    const playerPos = player.getComponent(HexPosition).coords;
    const action = Action.fromRelativeJSON(relativeActionData, player, playerPos);

    await this.submitPlayerAction(action);
  }
}
